/**
 * Off-Route Recalculation Hook for FlowRide Frontend
 * Monitors when user goes off route and triggers itinerary recalculation after 30 seconds
 */

import { useIonToast } from "@ionic/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ensureOffRouteRecalculationFlowId } from "../services/http/correlationFlowStore";
import { ItineraryService, TransportMode } from "../services/ItineraryService";
import { ephemeralStore, itineraryPanelStateSlice, navigationSessionSlice, useEphemeralSelector } from "../store/ephemeral";
import { bikePrefsSlice, carPrefsSlice, rentalVehiclesPrefsSlice, usePersistedSliceSelector } from "../store/persistence";
import { toCarTransitAccessRequestFields } from "../tools/carPrefs.tools";
import { getUserLocation, subscribeToLocation, type LocationState } from "../tools/GpsLocationAdapter";
import { isRentalRoutingEnabled } from "../tools/rentalVehiclePrefs.tools";
import { type ItinerarySearchRequestDTO } from "../types/dtos/api/itinerary/itinerary-search-request.dto";
import { ItineraryType, type ItineraryDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import { type RouteParametersDTO } from "../types/dtos/api/itinerary/route.dto";

interface OffRouteRecalculationOptions {

    /** Current active itinerary */
    "currentItinerary": ItineraryDTO | null;

    /** Callback when new itinerary is calculated */
    "onItineraryRecalculated": ( newItinerary: ItineraryDTO ) => void;

    /** Whether recalculation is enabled */
    "enabled"?: boolean;

    /** Time in milliseconds before triggering recalculation (default: 30000) */
    "offRouteTimeout"?: number;
}

/**
 * Hook to monitor off-route status and trigger itinerary recalculation
 */
export const useOffRouteRecalculation = ( {
    currentItinerary,
    onItineraryRecalculated,
    enabled = true,
    offRouteTimeout = 30000,
}: OffRouteRecalculationOptions ) => {
    const isOnLine = useEphemeralSelector(
        navigationSessionSlice,
        ( session ) => session.routeLineFollowing?.onLine,
    );
    const setLoadingItinerary = useCallback( ( loading: boolean ): void => {
        ephemeralStore.setValue( itineraryPanelStateSlice, ( previous ) => ( {
            ...previous,
            "loadingItinerary": loading,
        } ) );
    }, [] );
    const ensureOffRouteRecalculationFlow = useCallback( (): string => ensureOffRouteRecalculationFlowId(), [] );
    const elevationCosts = usePersistedSliceSelector(
        bikePrefsSlice,
        ( prefs ) => prefs.elevationCosts,
    );
    const enabledRentalVehicleTypes = usePersistedSliceSelector(
        rentalVehiclesPrefsSlice,
        ( prefs ) => prefs.enabledTypes,
    );
    const hasRentalTypes = isRentalRoutingEnabled( enabledRentalVehicleTypes );
    const carPrefs = usePersistedSliceSelector( carPrefsSlice, ( prefs ) => prefs );
    const [ userLocation, setUserLocation ] = useState<LocationState>( getUserLocation() );
    const [ presentToast ] = useIonToast();

    // Subscribe to GPS location updates
    useEffect( () => {
        const unsubscribe = subscribeToLocation( setUserLocation );

        return unsubscribe;
    }, [] );

    const offRouteTimerRef = useRef<NodeJS.Timeout | null>( null );
    const lastOnLineStateRef = useRef<boolean>( true );
    const isRecalculatingRef = useRef<boolean>( false );
    const itineraryService = useRef<ItineraryService>( new ItineraryService() );

    /**
     * Calculate new itinerary from current position to destination
     */
    const recalculateItinerary = useCallback( async() => {
        if ( !currentItinerary || !userLocation.latitude || !userLocation.longitude || isRecalculatingRef.current ) {
            return;
        }

        isRecalculatingRef.current = true;
        setLoadingItinerary( true );

        try {
            // Get destination from the last leg of current itinerary
            const lastLeg = currentItinerary.legs[currentItinerary.legs.length - 1];
            if (
                lastLeg === null ||
                lastLeg === undefined ||
                lastLeg.to?.location.lat === null ||
                lastLeg.to?.location.lat === undefined ||
                lastLeg.to?.location.lon === null ||
                lastLeg.to?.location.lon === undefined
            ) {
                console.warn( "[OffRouteRecalculation] No valid destination found in current itinerary" );

                return;
            }

            let newItinerary: ItineraryDTO | null = null;

            // Determine mode and method based on current itinerary type
            if ( currentItinerary.type === ItineraryType.CAR ) {
                // For car itineraries, use getCarItinerary
                const routeParams: RouteParametersDTO = {
                    "source": {
                        "lat": userLocation.latitude,
                        "lon": userLocation.longitude,
                    },
                    "destination": {
                        "lat": lastLeg.to.location.lat,
                        "lon": lastLeg.to.location.lon,
                    },
                    "type":                 "default",
                    "timestamp":            Date.now(),
                    "isDepartureTimestamp": true,
                };

                const response = await itineraryService.current.getCarItinerary( routeParams );
                newItinerary = response.data as ItineraryDTO;
            } else {
                /*
                 * For non-car itineraries, use searchItinerary
                 * Default to TRANSIT for MOTIS and other types
                 */
                const modes: TransportMode[] = [ TransportMode.TRANSIT ];
                if ( hasRentalTypes && !modes.includes( TransportMode.RENTAL ) ) {
                    modes.push( TransportMode.RENTAL );
                }

                const searchRequest: ItinerarySearchRequestDTO = {
                    "from": {
                        "lat":     userLocation.latitude,
                        "lon":     userLocation.longitude,
                        "country": "CH",
                    },
                    "to": {
                        "lat":     lastLeg.to.location.lat,
                        "lon":     lastLeg.to.location.lon,
                        "country": "CH",
                    },
                    "time": {
                        "timestamp":            Date.now(),
                        "isDepartureTimestamp": true,
                    },
                    "modes":              modes,
                    "rentalVehicleTypes": hasRentalTypes ? [ ...enabledRentalVehicleTypes ] : [],
                    "elevationCosts":     elevationCosts,
                    ...toCarTransitAccessRequestFields( carPrefs ),
                };

                /*
                 * console.log( "[OffRouteRecalculation] Recalculating itinerary from current position:", {
                 *    "source":             searchRequest.from,
                 *    "destination":         searchRequest.to,
                 *    "currentItineraryId": currentItinerary.type,
                 * } );
                 */

                ensureOffRouteRecalculationFlow();
                const results = await itineraryService.current.searchItinerary( searchRequest, { "flowCorrelationScope": "offRoute" } );
                const values = Object.values( results.itineraries );
                newItinerary = values.length > 0 ? values[0] : null;
            }

            if ( newItinerary !== null && newItinerary !== undefined && newItinerary.legs !== null && newItinerary.legs !== undefined && newItinerary.legs.length > 0 ) {
                /*
                 * console.log( "[OffRouteRecalculation] New itinerary calculated successfully:", {
                 *     "duration":  newItinerary.duration,
                 *     "legsCount": newItinerary.legs.length,
                 *     "startTime": newItinerary.startTime,
                 *     "endTime":   newItinerary.endTime,
                 *  } );
                 */

                // Notify user
                await presentToast( {
                    "message":  "Itinéraire recalculé depuis votre position actuelle",
                    "duration": 3000,
                    "position": "top",
                    "color":    "success",
                } );

                // Call the callback with new itinerary
                onItineraryRecalculated( newItinerary );
            } else {
                console.warn( "[OffRouteRecalculation] Invalid itinerary received from API" );
                await presentToast( {
                    "message":  "Impossible de recalculer l'itinéraire",
                    "duration": 3000,
                    "position": "top",
                    "color":    "warning",
                } );
            }
        } catch( error ) {
            console.error( "[OffRouteRecalculation] Error recalculating itinerary:", error );
            await presentToast( {
                "message":  "Erreur lors du recalcul de l'itinéraire",
                "duration": 3000,
                "position": "top",
                "color":    "danger",
            } );
        } finally {
            isRecalculatingRef.current = false;
            setLoadingItinerary( false );
        }
    }, [ carPrefs, currentItinerary, elevationCosts, enabledRentalVehicleTypes, ensureOffRouteRecalculationFlow, hasRentalTypes, onItineraryRecalculated, presentToast, setLoadingItinerary, userLocation ] );

    /**
     * Start the off-route timer
     */
    const startOffRouteTimer = useCallback( () => {
        if ( offRouteTimerRef.current ) {
            clearTimeout( offRouteTimerRef.current );
        }

        // console.log( "[OffRouteRecalculation] Starting off-route timer:", offRouteTimeout + "ms" );

        offRouteTimerRef.current = setTimeout( () => {
            // console.log( "[OffRouteRecalculation] Off-route timeout reached, recalculating itinerary" );
            void recalculateItinerary();
        }, offRouteTimeout );
    }, [ offRouteTimeout, recalculateItinerary ] );

    /**
     * Stop the off-route timer
     */
    const stopOffRouteTimer = useCallback( () => {
        if ( offRouteTimerRef.current ) {
            // console.log( "[OffRouteRecalculation] Stopping off-route timer" );
            clearTimeout( offRouteTimerRef.current );
            offRouteTimerRef.current = null;
        }
    }, [] );

    /**
     * Monitor following state changes
     */
    useEffect( () => {
        if ( !enabled || isOnLine === undefined ) {
            return;
        }

        const wasOnLine = lastOnLineStateRef.current;

        /*
         * console.log( "[OffRouteRecalculation] Following state changed:", {
         *     isOnLine,
         *     wasOnLine,
         *     "confidence": followingState.confidence,
         *     "lineId":     followingState.lineId,
         *  } );
         */

        // // User went off route
        if ( wasOnLine && !isOnLine ) {
            // console.log( "[OffRouteRecalculation] User went off route, starting timer" );
            startOffRouteTimer();
        } else  if ( !wasOnLine && isOnLine ) {
            /*
             * User got back on route
             * console.log( "[OffRouteRecalculation] User back on route, stopping timer" );
             */
            stopOffRouteTimer();
        }

        // Update last state
        lastOnLineStateRef.current = isOnLine;
    }, [ isOnLine, enabled, startOffRouteTimer, stopOffRouteTimer ] );

    /**
     * Cleanup on unmount
     */
    useEffect( () => {
        return () => {
            stopOffRouteTimer();
        };
    }, [ stopOffRouteTimer ] );

    /**
     * Reset timer when itinerary changes
     */
    useEffect( () => {
        if ( currentItinerary ) {
            stopOffRouteTimer();
            lastOnLineStateRef.current = true; // Reset to on-route state
        }
    }, [ currentItinerary, stopOffRouteTimer ] );

    return {
        "isRecalculating": isRecalculatingRef.current,
        startOffRouteTimer,
        stopOffRouteTimer,
    };
};
